import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Gavel, Clock, Users } from 'lucide-react';
import { formatCurrency, getResponsiveThumbnailUrl } from '@/lib/utils';

const DashboardBidsTab = ({ auctions }) => {
    const navigate = useNavigate();

    const getTimeRemaining = (endDate) => {
        if (!endDate) return 'N/A';
        const end = new Date(endDate);
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0) return 'Încheiată';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days}z ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    if (auctions.length === 0) {
        return (
            <Card className="rounded-2xl">
                <CardContent className="py-12 text-center">
                    <Gavel className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Nu participi la nicio licitație</h3>
                    <p className="text-muted-foreground mb-4">Explorează licitațiile disponibile și plasează oferte</p>
                    <Button className="rounded-full" onClick={() => navigate('/')}>
                        Explorează licitații
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-4">
                <div>
                    <CardTitle className="flex items-center gap-2">
                        <Gavel className="w-5 h-5 text-primary" />
                        Licitații
                    </CardTitle>
                    <CardDescription>
                        {auctions.length} licitații la care participi
                    </CardDescription>
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {auctions.map(auction => {
                        const thumbnailUrl = auction.images?.[0] ? getResponsiveThumbnailUrl(auction.images[0], 200, 150, 75) : null;
                        return (
                            <Card 
                                key={auction.listing_id} 
                                className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => navigate(`/listing/${auction.slug || auction.listing_id}`)}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            {thumbnailUrl ? (
                                                <img
                                                    src={thumbnailUrl}
                                                    alt={auction.title}
                                                    className="w-16 h-16 rounded-xl object-cover"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center">
                                                    <Gavel className="w-8 h-8 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-semibold">{auction.title}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Oferta ta: {formatCurrency(auction.my_highest_bid)}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {auction.is_winning ? (
                                                        <Badge className="bg-green-50 text-green-700">Câștigător momentan</Badge>
                                                    ) : (
                                                        <Badge className="bg-orange-50 text-orange-700">Depășit</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-primary">{formatCurrency(auction.current_price)}</p>
                                            <p className="text-xs text-muted-foreground">Preț curent</p>
                                            {auction.status === 'active' && (
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 justify-end">
                                                    <Clock className="w-3 h-3" />
                                                    {getTimeRemaining(auction.end_date)}
                                                </div>
                                            )}
                                            {auction.status === 'sold' && (
                                                <Badge className="mt-1 bg-blue-50 text-blue-700">Finalizată</Badge>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};

export default DashboardBidsTab;
